import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const selectClass = "w-full rounded border border-line bg-surface p-2 text-sm";

// Adota um ou mais containers Discovered — organizacionalmente (Project) e,
// opcionalmente, vinculados a um Site (o que passa a permitir Start/Stop/
// Restart reais via Client Portal e aqui). Nunca cria/altera o recurso no
// Docker — só registra a quem ele pertence no Control Plane.
export function AdoptContainerDialog({ open, onOpenChange, serverId, containerIds, onAdopted }) {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [sites, setSites] = useState([]);
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [environmentId, setEnvironmentId] = useState("");
  const [linkToSite, setLinkToSite] = useState(false);
  const [siteId, setSiteId] = useState("");
  const [role, setRole] = useState("web");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setClientId(""); setProjectId(""); setEnvironmentId(""); setSiteId(""); setLinkToSite(false); setRole("web"); setError("");
    Promise.all([http.get("/clients"), http.get("/projects")]).then(([c, p]) => { setClients(c); setProjects(p); });
  }, [open]);

  useEffect(() => {
    if (!projectId) { setEnvironments([]); setEnvironmentId(""); return; }
    http.get(`/projects/${projectId}/environments`).then(setEnvironments);
  }, [projectId]);

  useEffect(() => {
    if (!environmentId) { setSites([]); setSiteId(""); return; }
    http.get("/sites").then((all) => setSites(all.filter((s) => s.environment_id === environmentId)));
  }, [environmentId]);

  const projectsForClient = useMemo(
    () => (clientId ? projects.filter((p) => p.client_id === clientId) : projects),
    [projects, clientId],
  );

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { project_id: projectId || null, site_id: linkToSite && siteId ? siteId : null, role };
      if (containerIds.length === 1) {
        await http.post(`/servers/${serverId}/containers/${containerIds[0]}/adopt`, body);
      } else {
        await http.post(`/servers/${serverId}/containers/adopt-batch`, { resource_ids: containerIds, ...body });
      }
      onOpenChange(false);
      onAdopted?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const count = containerIds.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-line bg-popover">
        <DialogHeader>
          <DialogTitle>Adotar {count > 1 ? `${count} containers` : "container"}</DialogTitle>
          <DialogDescription>
            Registra a quem este recurso pertence no Control Plane — nunca recria ou altera nada no Docker.
            {count > 1 && " O mesmo Project/Site será aplicado a todos os selecionados; o papel (role) de cada um pode ser ajustado depois."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label>Cliente (filtro opcional)</Label>
            <select aria-label="Cliente" className={selectClass} value={clientId} onChange={(e) => { setClientId(e.target.value); setProjectId(""); }}>
              <option value="">Todos</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Projeto</Label>
            <select aria-label="Projeto" className={selectClass} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">Nenhum (só marca como adotado)</option>
              {projectsForClient.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={linkToSite} disabled={!projectId} onChange={(e) => setLinkToSite(e.target.checked)} />
            Vincular a um Site (habilita Start/Stop/Restart pelo Portal do Cliente)
          </label>

          {linkToSite && (
            <>
              <div>
                <Label>Environment</Label>
                <select aria-label="Environment" className={selectClass} value={environmentId} onChange={(e) => setEnvironmentId(e.target.value)}>
                  <option value="">Selecionar</option>
                  {environments.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Site</Label>
                <select aria-label="Site" className={selectClass} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                  <option value="">Selecionar</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Papel (role)</Label>
                <Input aria-label="Papel" value={role} onChange={(e) => setRole(e.target.value)} placeholder="web / api / worker / database" className="border-line bg-surface" />
              </div>
            </>
          )}

          {error && <p role="alert" className="text-sm text-err">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-line bg-transparent hover:bg-white/5">Cancelar</Button>
          <Button disabled={busy || (linkToSite && !siteId)} onClick={submit} className="bg-brand text-white hover:bg-brand-hover" data-testid="adopt-confirm">
            {busy ? "Adotando..." : "Adotar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
